import { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { useMutation } from '@apollo/client';

import { CreatePosMedUserDocument } from 'src/gql/graphql';
import type { CreatePosMedUserInput, IdentityConsumer } from 'src/gql/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import { useInstoreEnrollment } from 'src/hooks/use-instore-enrollment';
import { PersonalInfoFieldsType, personalInfoSchema } from './personal-information-step';
import { AddressFieldsType, addressSchema } from './address-step';
import { GoodCannaFieldsType } from './good-canna-step';
import { goodCannaSchema } from './good-canna-schema';
import { useInstoreEnrollmentAnalytics } from '../hooks/use-instore-enrollment-analytics';

type UserProfile = Pick<IdentityConsumer['profile'], 'birthday' | 'firstName' | 'lastName' | 'phone'>;

type FormData = {
  personalInfo: PersonalInfoFieldsType;
  address: AddressFieldsType;
  goodCanna: GoodCannaFieldsType;
  waitingAtDispensary: boolean;
};

type UseMedIntakeReturn = {
  step: number;
  formData: FormData;
  isSubmitting: boolean;
  submissionError: string | null;
  submissionSuccess: boolean;
  nextStep: () => void;
  previousStep: () => void;
  handleStepSubmit: (stepData: Partial<FormData>) => void;
  handleFinalSubmit: (stepData: Partial<FormData>) => Promise<void>;
};

const mapUserProfileToPersonalInfo = (
  profile: UserProfile | undefined,
  email: string | null
): Partial<PersonalInfoFieldsType> => {
  const mappedData = {
    ...(profile?.firstName && { firstName: profile.firstName }),
    ...(profile?.lastName && { lastName: profile.lastName }),
    ...(profile?.phone && { phone: profile.phone }),
    ...(profile?.birthday && { dateOfBirth: profile.birthday }),
    ...(email && { email }),
  } as Partial<PersonalInfoFieldsType>;

  return mappedData;
};

/**
 * Creates default form data with user profile data prefilled when available
 */
const createDefaultFormData = (profile: UserProfile | undefined, email: string | null): FormData => {
  const schemaDefaults = personalInfoSchema?.cast({}) || ({} as PersonalInfoFieldsType);
  const userProfileData = mapUserProfileToPersonalInfo(profile, email);

  return {
    personalInfo: { ...schemaDefaults, ...userProfileData } as PersonalInfoFieldsType,
    address: (addressSchema?.cast({}) || {}) as AddressFieldsType,
    goodCanna: goodCannaSchema?.cast({}) ?? {},
    waitingAtDispensary: false,
  };
};

/**
 * Flattens the nested form data structure to match the GraphQL createPOSMedUserInput schema
 */
const flattenFormData = (formData: FormData): CreatePosMedUserInput => {
  const { personalInfo, address, goodCanna, waitingAtDispensary } = formData;

  return {
    waitingAtDispensary,
    ...personalInfo,
    ...address,
    ...goodCanna,
  };
};

/**
 * Custom hook for managing medical intake form state and navigation
 * Handles step progression, form data accumulation, and final submission
 */
export const useMedIntake = (): UseMedIntakeReturn => {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const enrollmentStore = useInstoreEnrollment();
  const { dispensary } = useDispensary();
  const [createPosMedUser] = useMutation(CreatePosMedUserDocument);
  const user = useUser();
  const { trackMedCardCompleted, trackMedCardFailed, logError } = useInstoreEnrollmentAnalytics();

  // Initialize form data with user profile data when available
  const [formData, setFormData] = useState<FormData>(() => createDefaultFormData(user?.profile, user?.email));

  // Update form data when user profile data becomes available
  useEffect(() => {
    if (user?.profile || user?.email) {
      const updatedFormData = createDefaultFormData(user?.profile, user?.email);
      setFormData(updatedFormData);
    }
  }, [user?.profile, user?.email]);

  // Clear any stale phone data when the hook initializes (only once)
  useEffect(() => {
    if (enrollmentStore?.setUserPhone) {
      enrollmentStore.setUserPhone(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const nextStep = useCallback((): void => {
    setStep((prev) => prev + 1);
  }, []);

  const previousStep = useCallback((): void => {
    setStep((prev) => prev - 1);
  }, []);

  const handleStepSubmit = useCallback(
    (stepData: Partial<FormData>): void => {
      setFormData((prev) => ({ ...prev, ...stepData }));
      nextStep();
    },
    [nextStep]
  );

  const handleFinalSubmit = useCallback(
    async (stepData: Partial<FormData>): Promise<void> => {
      // Extract preferredDispensary from URL at submission time
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
      const preferredDispensary = typeof router?.query.cName === 'string' ? router?.query.cName : '';

      // Combine all form data and submit
      const finalData = {
        ...formData,
        ...stepData,
        goodCanna: {
          ...formData.goodCanna,
          ...stepData.goodCanna,
          // Add preferredDispensary from URL
          preferredDispensary,
        },
      };
      setFormData(finalData);

      // Save phone number to the store
      if (finalData.personalInfo?.phone) {
        enrollmentStore.setUserPhone(finalData.personalInfo.phone);
      }

      // Ensure we have a dispensary ID before submitting
      if (!dispensary?.id) {
        logError('No dispensary ID available for POS med user creation', { step: 'medIntake' });
        setSubmissionError('No dispensary ID available for POS med user creation');
        return;
      }

      setIsSubmitting(true);
      setSubmissionError(null);
      setSubmissionSuccess(false);

      // Advance to submitted step immediately to show loading state
      nextStep();

      try {
        // Flatten the form data to match the GraphQL schema
        const flattenedData = flattenFormData(finalData);

        const result = await createPosMedUser({
          variables: {
            dispensaryId: dispensary.id,
            user: flattenedData,
          },
        });

        // Check if the submission was successful
        if (result.data?.createPOSMedUser?.success === false) {
          const errorMsg = result.data.createPOSMedUser.message || 'Submission failed';
          trackMedCardFailed('api_error', errorMsg);
          setSubmissionError(errorMsg);
          setSubmissionSuccess(false);
          enrollmentStore.setCompletedMedIntake(false);
        } else {
          trackMedCardCompleted(finalData.waitingAtDispensary);
          setSubmissionSuccess(true);
          enrollmentStore.setCompletedMedIntake(true);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        trackMedCardFailed('network_error', errorMessage);
        setSubmissionError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      enrollmentStore,
      dispensary?.id,
      createPosMedUser,
      nextStep,
      router?.query.cName,
      logError,
      trackMedCardCompleted,
      trackMedCardFailed,
    ]
  );

  return {
    step,
    formData,
    isSubmitting,
    submissionError,
    submissionSuccess,
    nextStep,
    previousStep,
    handleStepSubmit,
    handleFinalSubmit,
  };
};
